import { Button, FormField, SwitchField } from '@dev-spendesk/grapes';
import { useFormik } from 'formik';

import { AutocompleteMultipleSearch } from 'src/core/common/components/AutocompleteSearch';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { type FormErrors } from 'src/core/common/utils/forms/types';

import { useOrganisationCompanyList } from '../../../../hooks/data/useOrganisationCompanyList';
import { useOrganisationSettings } from '../../hooks/useOrganisationSettings';
import { type Setting } from '../../setting';

export type FormValues = {
  companyIds: string[];
  autoAssignFutureEntities: boolean;
};

type Props = {
  onComplete(values: FormValues): void;
  onCancel(): void;
  mode: 'singleModal' | 'stepModal';
  setting?: Setting;
  costCenterName: string | undefined;
  isSubmitting: boolean;
};

export const CostCenterAssignmentContent = ({
  onComplete,
  onCancel,
  mode,
  costCenterName,
  setting,
  isSubmitting,
}: Props) => {
  const { t } = useTranslation('global');
  const companies = useOrganisationCompanyList();
  const organisationSettingsQueryState = useOrganisationSettings();

  const formikProps = useFormik<FormValues>({
    initialValues: {
      companyIds: setting?.companyIds ?? [],
      autoAssignFutureEntities: setting?.autoAssignFutureEntities ?? false,
    },
    validate: (values) => {
      const errors: FormErrors<FormValues> = {};
      const settings = organisationSettingsQueryState.data;
      const otherSettings = settings?.filter((s) => {
        // Exclude the current setting by comparing all relevant fields
        // because setting.id is not always available, it can be an empty string
        if (setting) {
          return !(
            s.name === setting.name &&
            JSON.stringify(s.companyIds) === JSON.stringify(setting.companyIds)
          );
        }
        return true;
      });

      const hasSameNameCostCenter = otherSettings?.find((s) => {
        return (
          s.name === costCenterName &&
          s.companyIds.some((companyId) =>
            values.companyIds.includes(companyId),
          )
        );
      });

      if (hasSameNameCostCenter) {
        errors.companyIds = t(
          'multiEntityHub.costCenters.modal.assignment.sameNameCostCenter',
        );
      }
      if (values.companyIds.length === 0) {
        errors.companyIds = t(
          'multiEntityHub.costCenters.modal.assignment.entitiesRequired',
        );
      }

      return errors;
    },
    onSubmit: (values) => {
      onComplete({
        ...values,
        autoAssignFutureEntities: values.autoAssignFutureEntities,
      });
    },
  });

  const companyOptions = companies.map((company) => ({
    key: company.id,
    label: company.name,
  }));

  const selectedOptions = companyOptions.filter((option) =>
    formikProps.values.companyIds.includes(option.key),
  );

  return (
    <>
      <FormField
        label={t('multiEntityHub.costCenters.modal.assignment.entities')}
        alertMessage={formikProps.errors.companyIds as string | undefined}
      >
        <AutocompleteMultipleSearch
          fit="parent"
          placeholder={t(
            'multiEntityHub.costCenters.modal.assignment.selectEntities',
          )}
          isInvalid={Boolean(formikProps.errors.companyIds)}
          options={companyOptions}
          values={selectedOptions}
          onSelect={(options) => {
            formikProps.setFieldValue(
              'companyIds',
              options.map((option) => option.key),
            );
          }}
          translations={{
            selectAll: t('misc.selectAll'),
            selected: selectedOptions
              .map(
                (option) =>
                  companies.find((company) => company.id === option.key)?.name,
              )
              .join(', '),
          }}
        />
      </FormField>

      <SwitchField
        fit="parent"
        className="mt-8"
        label={t(
          'multiEntityHub.costCenters.modal.assignment.addToFutureEntitiesTitle',
        )}
        helpText={t(
          'multiEntityHub.costCenters.modal.assignment.addToFutureEntitiesHelp',
        )}
        isChecked={formikProps.values.autoAssignFutureEntities}
        onChange={(event) =>
          formikProps.setFieldValue(
            'autoAssignFutureEntities',
            event.target.checked,
          )
        }
      />

      <div className="mt-16 -mb-24 flex w-full justify-center gap-16">
        <Button
          variant="secondaryNeutral"
          text={t('multiEntityHub.costCenters.modal.assignment.actions.back')}
          onClick={onCancel}
        />
        <Button
          variant="primaryBrand"
          className={isSubmitting ? 'cursor-progress' : ''}
          text={
            mode === 'singleModal'
              ? t('multiEntityHub.costCenters.modal.assignment.actions.confirm')
              : t('multiEntityHub.costCenters.modal.assignment.actions.submit')
          }
          onClick={() => !isSubmitting && formikProps.submitForm()}
          disabled={isSubmitting}
          isLoading={isSubmitting}
        />
      </div>
    </>
  );
};
